/**
 * Audytor WCAG (wcag.avangardo.pl) - przebieg badania w przegladarce.
 *
 * Baza: audytor.js Radaru AI, przepisany 19.09.2026 na jedno pole adresu (wzor:
 * localone-audytor i wpplan-audytor). Kolejnosc akcji API (docs/ARCHITEKTURA.md):
 * resolve_site -> check_html -> check_lighthouse -> get_summary -> save_email.
 * Tresc zgody TRESC_ZGODY musi byc IDENTYCZNA z api.php (silnik).
 */
export function audytor() {
    const form = document.getElementById('analyzeForm');
    if (!form) return; // formularz tylko na stronie glownej (mainsite.php)

    // Warstwa danych jak w pozostalych audytorach: audit_requested, audit_completed,
    // report_submitted. Bez nich regula "Konwersja lead magnetu" we wspolnym
    // kontenerze GTM nie ma sie o co zaczepic.
    function pushDL(name, params) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
    }

    let progress = 0;
    const preloader = document.querySelector('.preloader');
    const preloaderData = document.querySelector('.preloader__data');
    const resultsContainer = document.getElementById('test_results');
    const urlInput = document.getElementById('url');
    const urlError = document.getElementById('urlError');
    const appHeading = document.querySelector('.app__testinfoContent h2');

    const API_URL = 'wp-content/themes/audytor/api.php';
    const app = document.querySelector('.app');
    const appHeader = document.querySelector('.app__header');
    const przerwijBtn = document.querySelector('.app__przerwij');
    let biezacyCtrl = null;   // zapytanie w toku, do przerwania przyciskiem albo Escape
    let przerwane = false;

    /**
     * Wpis do dziennika bledow (24.09.2026). window.wcagDziennik pochodzi ze skryptu
     * w wp_head (functions.php); gdy go nie ma, logowanie cicho odpada, strona dziala dalej.
     * Odpowiedzi JSON ze statusem error serwer zapisuje sam, tu trafia tylko to, czego
     * serwer nie widzi: siec, limit czasu, zepsuta odpowiedz, wyjatki w przegladarce.
     */
    function zaloguj(etap, typ, szczegol, komunikatKlienta, kod) {
        try {
            if (typeof window.wcagDziennik === 'function') window.wcagDziennik(etap, typ, szczegol, komunikatKlienta, kod);
        } catch (e) { /* dziennik nie moze psuc audytora */ }
    }

    /** Wpis z bledu rzuconego przez apiCall (pola typ, kod, techniczny) albo z dowolnego wyjatku. */
    function zalogujBlad(etap, e, komunikatKlienta) {
        const typ = e && e.typ ? e.typ : (e && e.name === 'AbortError' ? 'timeout' : 'wyjatek_js');
        const szczegol = e && e.techniczny ? e.techniczny : e;
        zaloguj(etap, typ, szczegol, komunikatKlienta, e && e.kod);
    }

    function bladApi(komunikat, typ, kod, techniczny) {
        const e = new Error(komunikat);
        e.typ = typ;
        e.kod = kod;
        e.techniczny = techniczny;
        return e;
    }

    // Odpowiedz spoza naszego kodu (blad posrednika, przekroczony czas, awaria PHP)
    // nie jest JSON-em. Zamiast komunikatu parsera uzytkownik dostaje zdanie po polsku.
    async function odczytajOdpowiedz(response) {
        const tekst = await response.text();
        try {
            return JSON.parse(tekst);
        } catch (e) {
            const opis = 'HTTP ' + response.status + ', odpowiedź nie jest JSON: ' + tekst.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
            if (response.status === 524 || response.status === 504 || response.status === 408) {
                throw bladApi('Badanie trwa dłużej niż zwykle i połączenie zostało przerwane. Spróbuj jeszcze raz za chwilę.', 'timeout', response.status, opis);
            }
            if (response.status >= 500) {
                throw bladApi('Nasz serwer nie dokończył badania. Spróbuj ponownie za kilka minut.', 'http_5xx', response.status, opis);
            }
            throw bladApi('Nie udało się odczytać odpowiedzi serwera. Spróbuj ponownie za chwilę.', response.status >= 400 ? 'http_4xx' : 'odpowiedz', response.status, opis);
        }
    }

    const KOMUNIKAT_SIECI = 'Nie udało się połączyć z audytorem. Sprawdź połączenie i spróbuj ponownie.';

    // Fetch z twardym timeoutem (AbortController). Lighthouse na trzech stronach
    // przez DataForSEO potrafi trwac do ~2 min, stad dlugie limity przy tym kroku.
    async function apiCall(payload, timeoutMs = 45000) {
        const ctrl = new AbortController();
        biezacyCtrl = ctrl;
        const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
        const start = Date.now();
        try {
            let response;
            try {
                response = await fetch(API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    signal: ctrl.signal
                });
            } catch (e) {
                // Przerwanie przyciskiem albo limitem czasu zostawiamy (AbortError ma wlasne
                // komunikaty). Kazda inna awaria fetch to "Failed to fetch" po angielsku (E2E 20.09, B9).
                if (e && e.name === 'AbortError') {
                    e.techniczny = 'Przekroczony limit ' + Math.round(timeoutMs / 1000) + ' s po ' + Math.round((Date.now() - start) / 1000) + ' s';
                    throw e;
                }
                throw bladApi(KOMUNIKAT_SIECI, 'siec', '', (e && e.message) || 'fetch odrzucony');
            }
            return await odczytajOdpowiedz(response);
        } finally {
            window.clearTimeout(timer);
            if (biezacyCtrl === ctrl) biezacyCtrl = null;
        }
    }

    // Przerwanie badania od pierwszej sekundy: przycisk w naglowku nakladki albo Escape.
    // Zapytanie w toku dostaje abort, stan wraca do zera, fokus wraca na pole adresu.
    function przerwijBadanie() {
        if (!app.classList.contains('show') || appHeader.classList.contains('step2')) return;
        przerwane = true;
        if (biezacyCtrl) biezacyCtrl.abort();
        app.classList.remove('show');
        document.querySelector('body').classList.remove('showMenu');
        resetAppState();
        urlInput.focus();
    }
    if (przerwijBtn) przerwijBtn.addEventListener('click', przerwijBadanie);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') przerwijBadanie();
    });

    // --- pole adresu -------------------------------------------------------

    function normalizeUrl(value) {
        let v = String(value || '').trim();
        v = v.replace(/^[\s"'<(\[]+/, '').replace(/[\s"'>)\]]+$/, '');
        if (!v) return '';
        if (!/^https?:\/\//i.test(v)) {
            if (/^[a-z][a-z0-9+.\-]*:/i.test(v)) return v; // obcy schemat: walidacja odrzuci
            v = 'https://' + v;
        }
        return v;
    }

    function isValidUrl(value) {
        try {
            const u = new URL(value);
            if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
            return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(u.hostname);
        } catch (e) {
            return false;
        }
    }

    function displayError(message) {
        urlError.textContent = message;
        urlError.hidden = false;
        urlInput.setAttribute('aria-invalid', 'true');
        urlInput.focus();
    }

    function clearError() {
        urlError.textContent = '';
        urlError.hidden = true;
        urlInput.removeAttribute('aria-invalid');
        const alert = document.querySelector('.subpageHeader .error-alert');
        if (alert) alert.remove();
    }

    urlInput.addEventListener('blur', () => {
        if (urlInput.value.trim()) urlInput.value = normalizeUrl(urlInput.value);
    });
    urlInput.addEventListener('input', () => {
        if (!urlError.hidden) clearError();
    });

    // --- przebieg badania ---------------------------------------------------

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        urlInput.value = normalizeUrl(urlInput.value);
        const url = urlInput.value;

        if (!url) {
            displayError('Wpisz adres strony, np. twojafirma.pl');
            return;
        }
        if (!isValidUrl(url)) {
            displayError('To nie wygląda na adres strony. Sprawdź pisownię, np. twojafirma.pl');
            return;
        }
        clearError();

        // Meta Pixel: start badania to ViewContent, NIE Lead. Lead pada dopiero przy
        // bramce z adresem e-mail (ustalenie 23.08.2026 dla wszystkich audytorow).
        if (typeof fbq === 'function') {
            fbq('track', 'ViewContent', { content_name: url, content_category: 'wcag_audit' });
        }
        pushDL('audit_requested', { audit_query: url });

        let host = url;
        try { host = new URL(url).hostname.replace(/^www\./i, ''); } catch (e) { /* zostaje pelny adres */ }

        przerwane = false;
        app.classList.add('show');
        document.querySelector('body').classList.add('showMenu');
        document.getElementById('siteUrl2').textContent = host;
        const elZbadane = document.getElementById('zbadane');   // zdjete pinezka 29, kod zostaje odporny
        if (elZbadane) elZbadane.textContent = '';

        resultsContainer.classList.add('active');
        preloader.classList.add('active');
        setAppHeader('Sprawdzam dostępność strony', 'Teraz wykonuję:');
        // Nakladka jest nowym ekranem: fokus na jej naglowku, zeby czytnik ekranu
        // nie zostal na przycisku formularza pod spodem.
        if (appHeading) window.setTimeout(() => appHeading.focus(), 50);
        setMiniStep(null);
        showStepMessage('Pobieram stronę');
        updatePreloaderData(6, 500);

        // Krok 1: adres, kanonizacja, HTML strony glownej i wybor podstron
        let resolveData;
        try {
            resolveData = await apiCall({ action: 'resolve_site', url: url }, 60000);
        } catch (e) {
            if (przerwane) return;
            const komunikat = e.name === 'AbortError'
                ? 'Strona odpowiada wolniej niż zwykle i przekroczyliśmy limit czasu. Wróć i spróbuj ponownie.'
                : (e.typ ? e.message : 'Wystąpił błąd połączenia. Spróbuj ponownie.');
            zalogujBlad('resolve_site', e, komunikat);
            showFatalError(komunikat);
            return;
        }
        if (resolveData.status === 'error_alert') {
            showAlertError(resolveData.message);
            return;
        }
        if (resolveData.status !== 'ok') {
            showFatalError(resolveData.message || 'Nie udało się pobrać strony. Sprawdź adres i spróbuj ponownie.');
            return;
        }
        const daneStrony = resolveData.data || {};
        if (daneStrony.host) document.getElementById('siteUrl2').textContent = daneStrony.host;
        const liczbaStron = Array.isArray(daneStrony.pages) ? daneStrony.pages.length : 0;
        const elZbadane2 = document.getElementById('zbadane');
        if (liczbaStron && elZbadane2) elZbadane2.textContent = 'Zbadane: ' + (Array.isArray(daneStrony.pages_labels) && daneStrony.pages_labels.length === liczbaStron ? daneStrony.pages_labels.map((e, i) => i === 0 ? e.toLowerCase() : e).join(', ') : opiszStrony(daneStrony.pages));
        updatePreloaderData(12, 500);

        // Krok 2-4: testy wlasne, Lighthouse, wynik
        const actions = [
            { name: 'check_html', krok: 'html', description: 'Sprawdzam strukturę, formularze i dokumenty', timeout: 90000, cel: 30, czas: 1200 },
            { name: 'check_lighthouse', krok: 'lighthouse', description: liczbaStron > 1 ? ('Badam ' + liczbaStron + ' ' + odmiana(liczbaStron, 'kluczową stronę', 'kluczowe strony', 'kluczowych stron')) : 'Badam stronę główną', timeout: 200000, cel: 84, czas: 45000 },
            { name: 'get_summary', krok: 'wynik', description: 'Liczę wynik', timeout: 60000, cel: 100, czas: 800 }
        ];

        let poprzedniCel = 12;
        for (const action of actions) {
            showStepMessage(action.description);
            setMiniStep(action.krok);
            // Licznik nie moze zostac w tyle za krokiem: najpierw dochodzi do celu
            // poprzedniego kroku, potem pelznie ku biezacemu (Lighthouse trwa do 2 min).
            ustawProcent(poprzedniCel);
            updatePreloaderData(action.cel - 2, action.czas);
            poprzedniCel = action.cel;

            let data;
            try {
                data = await apiCall({ action: action.name }, action.timeout);
            } catch (e) {
                if (przerwane) return;
                const komunikat = e.name === 'AbortError'
                    ? 'Badanie trwa dłużej niż zwykle i przekroczyliśmy limit czasu. Wróć i spróbuj ponownie.'
                    : (e.typ ? e.message : 'Wystąpił błąd połączenia. Spróbuj ponownie.');
                zalogujBlad(action.name, e, komunikat);
                showFatalError(komunikat);
                return;
            }

            if (data.status === 'ok') {
                progress = action.cel;
                updatePreloaderData(progress, 500);
                if (action.name === 'get_summary') {
                    try {
                        showResults(data);
                    } catch (e) {
                        const komunikat = 'Nie udało się wyświetlić wyniku. Odśwież stronę albo uruchom badanie ponownie.';
                        zalogujBlad('showResults', e, komunikat);
                        showFatalError(komunikat);
                    }
                    return;
                }
            } else if (data.status === 'error_alert') {
                showAlertError(data.message);
                return;
            } else {
                showFatalError(data.message || 'Wystąpił błąd podczas badania. Spróbuj ponownie.');
                return;
            }
        }
    });

    /** Lista zbadanych stron po ludzku: strona glowna plus sciezki podstron. */
    function opiszStrony(pages) {
        return pages.map((u) => {
            try {
                const x = new URL(u);
                const sciezka = x.pathname.replace(/\/+$/, '');
                return sciezka && sciezka !== '/' ? sciezka + '/' : 'strona główna';
            } catch (e) { return u; }
        }).join(', ');
    }

    /** Mini atrapa w preloaderze: etap w toku i etapy zakonczone. */
    function setMiniStep(name) {
        const kroki = ['html', 'lighthouse', 'wynik'];
        const idx = kroki.indexOf(name);
        kroki.forEach((k, i) => {
            const g = document.querySelector('.rb-mini__krok[data-krok="' + k + '"]');
            if (!g) return;
            g.classList.remove('active', 'done');
            if (idx < 0) return;
            if (i < idx) g.classList.add('done');
            if (i === idx) g.classList.add('active');
        });
    }

    function setAppHeader(title, label) {
        if (appHeading) appHeading.textContent = title;
        const stepLabel = document.getElementById('stepLabel');
        if (stepLabel) stepLabel.textContent = label;
    }

    function showStepMessage(text) {
        const messageElement = document.createElement('div');
        messageElement.textContent = text;
        messageElement.classList.add('result-message');
        const previousMessages = resultsContainer.querySelectorAll('.result-message');
        previousMessages.forEach(message => message.classList.add('hide'));
        resultsContainer.appendChild(messageElement);
        window.setTimeout(() => {
            messageElement.classList.add('show');
        }, 100);
    }

    function showFatalError(message) {
        // Komunikaty krokow znikaja calkiem: przy klasie hide etykieta kroku zostawala
        // pod naglowkiem "Nie udalo sie" i mowila o innym kroku (E2E 20.09).
        resultsContainer.querySelectorAll('.result-message').forEach(m => m.remove());
        document.body.classList.remove('wcag-wczytuje-raport');
        document.querySelector('.app__header').classList.add('stepCandidates');
        setAppHeader('Nie udało się', '');

        const holder = document.createElement('div');
        holder.classList.add('fatal-error');

        const text = document.createElement('p');
        text.classList.add('fatal-error__text');
        text.textContent = message;
        holder.appendChild(text);

        const back = document.createElement('button');
        back.type = 'button';
        back.classList.add('fatal-error__back');
        back.textContent = 'Wróć i spróbuj ponownie';
        back.addEventListener('click', () => {
            document.querySelector('.app').classList.remove('show');
            document.querySelector('body').classList.remove('showMenu');
            resetAppState();
            urlInput.focus();
        });
        holder.appendChild(back);
        resultsContainer.appendChild(holder);
        window.setTimeout(() => back.focus(), 50);
    }

    /** Limit badan albo budzetu: wracamy na ekran startowy z komunikatem pod polem. */
    function showAlertError(message) {
        document.body.classList.remove('wcag-wczytuje-raport');
        document.querySelector('.app').classList.remove('show');
        document.querySelector('body').classList.remove('showMenu');
        resetAppState();
        const stary = document.querySelector('.subpageHeader .error-alert');
        if (stary) stary.remove();
        const errorMessage = document.createElement('p');
        errorMessage.classList.add('error-alert');
        errorMessage.setAttribute('role', 'alert');
        errorMessage.textContent = message;
        // Kotwica: akapit wstepu zostal zdjety 20.09, wiec celujemy w formularz,
        // a jak i jego nie ma, w kolumne tekstu hero. Bez tego limit badan wywalal JS.
        const kotwica = document.querySelector('.subpageHeader__text')
            || document.querySelector('.subpageHeader .wcag-form')
            || document.querySelector('.wcag-hero__tekst');
        if (kotwica) {
            kotwica.insertAdjacentElement(kotwica.classList.contains('wcag-hero__tekst') ? 'beforeend' : 'beforebegin', errorMessage);
        }
        urlInput.focus();
    }

    window.wcagPokazAlert = showAlertError;

    function resetAppState() {
        progress = 0;
        preloader.classList.remove('active');
        preloaderData.textContent = '0%';
        resultsContainer.classList.remove('active');
        document.querySelector('.app').classList.remove('app--wynik');
        resultsContainer.querySelectorAll('.result-message, .fatal-error').forEach(el => el.remove());
        const finallyEl = document.querySelector('.app__finally');
        if (finallyEl) finallyEl.remove();
        const wynikGora = document.querySelector('.app__content > .wcag-wynik');
        if (wynikGora) wynikGora.remove();
        const pasek = document.querySelector('.app__bar');
        const gotoLink = appHeader.querySelector('.app__gotoform');
        if (pasek && gotoLink) { gotoLink.classList.remove('mainBtn', 'app__gotoform--naglowek'); pasek.appendChild(gotoLink); }
        if (pasek) pasek.hidden = false;
        if (preloaderTimer) { clearInterval(preloaderTimer); preloaderTimer = null; }
        document.querySelector('.app__header').classList.remove('step2', 'stepCandidates');
        setAppHeader('Sprawdzam dostępność strony', 'Teraz wykonuję:');
        setMiniStep(null);
    }

    // --- ekran wyniku -------------------------------------------------------

    // Ponizej 70 wynik jest czerwony: 40-69 to "istotne bariery", nie ostrzezenie (decyzja 19.09).
    function klasaStanu(score) {
        if (score < 70) return 'blad';
        if (score < 90) return 'uwaga';
        return 'ok';
    }

    function etykietaWyniku(score) {
        // Etykieta mowi, WOBEC CZEGO mierzymy (pinezka 31, 20.09): nazwa normy i poziom.
        if (score < 40) return 'Strona nie spełnia WCAG 2.1 AA i wyklucza część użytkowników';
        if (score < 70) return 'Strona ma istotne bariery wobec WCAG 2.1 AA';
        if (score < 90) return 'Strona częściowo spełnia WCAG 2.1 AA, wymaga poprawek';
        return 'Strona blisko zgodności z WCAG 2.1 na poziomie AA, do potwierdzenia przez eksperta';
    }

    function odmiana(n, j, m, w) {
        n = Math.abs(n);
        if (n === 1) return j;
        const r10 = n % 10, r100 = n % 100;
        if (r10 >= 2 && r10 <= 4 && (r100 < 12 || r100 > 14)) return m;
        return w;
    }

    /**
     * Naglowek wyniku (duza liczba, etykieta, liczniki, trzy najwazniejsze problemy)
     * rysowany z pol w korzeniu odpowiedzi get_summary, jesli silnik je podal.
     * Gdy ich nie ma, naglowek przychodzi w HTML z `data` i front go tylko styluje.
     */
    function zbudujNaglowekWyniku(data) {
        if (typeof data.score !== 'number' || !data.liczniki) return null;
        const score = Math.max(0, Math.min(100, Math.round(data.score)));
        const stan = klasaStanu(score);

        const box = document.createElement('div');
        box.className = 'wcag-wynik wcag-wynik--' + stan;

        const liczba = document.createElement('div');
        liczba.className = 'wcag-wynik__liczba';
        liczba.setAttribute('aria-label', 'Wynik ' + score + ' na 100');
        const b = document.createElement('b');
        b.textContent = '0';
        b.dataset.to = String(score);
        liczba.appendChild(b);
        const ze = document.createElement('span');
        ze.textContent = '/ 100';
        liczba.appendChild(ze);
        box.appendChild(liczba);

        const etykieta = document.createElement('div');
        etykieta.className = 'wcag-wynik__etykieta';
        // Adres badanej strony wpleciony w etykiete (pinezka 20, 20.09): wynik ma mowic,
        // CZYJA strona jest oceniana. Tekst z API zostaje nietkniety w PDF i mailu,
        // tutaj scinamy tylko wiodace slowo "Strona".
        const bazaEtykiety = data.etykieta || etykietaWyniku(score);
        const badanyHost = (document.getElementById('siteUrl2') || {}).textContent || '';
        etykieta.textContent = badanyHost
            ? badanyHost + ': ' + bazaEtykiety.replace(/^Strona /, '').replace(/^./, (z) => z.toLowerCase())
            : bazaEtykiety;
        box.appendChild(etykieta);

        const l = data.liczniki;
        const ul = document.createElement('ul');
        ul.className = 'wcag-liczniki';
        [
            ['blad', l.bledy, 'błąd', 'błędy', 'błędów'],
            ['uwaga', l.uwagi, 'uwaga', 'uwagi', 'uwag'],
            ['reczny', l.reczne, 'do sprawdzenia przez eksperta', 'do sprawdzenia przez eksperta', 'do sprawdzenia przez eksperta']
        ].forEach(([klucz, n, j, m, w]) => {
            const liczbaN = Number(n) || 0;
            const li = document.createElement('li');
            li.className = 'wcag-licznik wcag-licznik--' + klucz;
            const bb = document.createElement('b');
            bb.textContent = String(liczbaN);
            li.appendChild(bb);
            li.appendChild(document.createTextNode(' ' + odmiana(liczbaN, j, m, w)));
            ul.appendChild(li);
        });
        box.appendChild(ul);

        if (Array.isArray(data.top3) && data.top3.length) {
            const h = document.createElement('h3');
            h.className = 'wcag-top3__naglowek';
            h.textContent = 'Najważniejsze do naprawy';
            box.appendChild(h);
            const ol = document.createElement('ol');
            ol.className = 'wcag-top3';
            data.top3.slice(0, 3).forEach((p) => {
                const li = document.createElement('li');
                li.className = 'wcag-top3__item wcag-top3__item--' + (p.status === 'uwaga' ? 'uwaga' : 'blad');
                const t = document.createElement('span');
                t.className = 'wcag-top3__tytul';
                t.textContent = p.tytul || '';
                li.appendChild(t);
                if (p.kogo) {
                    const k = document.createElement('span');
                    k.className = 'wcag-top3__kogo';
                    k.textContent = 'dotyka: ' + (Array.isArray(p.kogo) ? p.kogo.join(', ') : p.kogo);
                    li.appendChild(k);
                }
                ol.appendChild(li);
            });
            box.appendChild(ol);
        }
        return box;
    }

    /**
     * Odnosnik do tego wyniku, zeby dalo sie go przeslac dalej, na przyklad do dzialu IT
     * placowki (dyspozycja Marcina 20.09.2026). Token bierzemy z serwera, wpisujemy go
     * w pasek adresu i dajemy przycisk kopiowania; bez zapisu w schowku nadal dziala,
     * bo adres w pasku jest juz pelny.
     */
    function dodajOdnosnikDoWyniku() {
        fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'share_report' })
        })
            .then((r) => r.json())
            .then((d) => {
                const token = d && d.data && d.data.token;
                if (!token) return;   // odmowe z serwera zapisuje api.php
                // Bez przycisku (pinezka 30): wystarczy, ze adres w pasku przegladarki
                // jest pelnym odnosnikiem do tego wyniku i da sie go skopiowac wprost.
                try { window.history.replaceState(null, '', '?r=' + token); } catch (e) { zalogujBlad('share_report', e, ''); }
            })
            // Brak odnosnika nie psuje ekranu wyniku, wiec klient nie dostaje komunikatu
            // (odnosnik jest dodatkiem); wpis w dzienniku zostaje.
            .catch((e) => { zaloguj('share_report', e && e.name === 'TypeError' ? 'siec' : 'odpowiedz', e, ''); });
    }

    function showResults(data) {
        // Wejscie z ?r=: nakladka oddaje ekran, wiec baner zgody moze sie juz pokazac.
        document.body.classList.remove('wcag-wczytuje-raport');
        // Blokada przewijania nalezy do ekranu ladowania (body.showMenu ma
        // overflow:hidden i height:100vh). Na ekranie wyniku raport ma 10 000 px
        // wysokosci, wiec bez zdjecia tej klasy strony nie da sie przewinac.
        document.body.classList.remove('showMenu');
        document.querySelector('.app__header').classList.add('step2');
        setAppHeader('', '');   // pinezka 22: pasek wyniku to nawigacja, nie komunikat

        // Pasek postepu i komunikaty krokow naleza do stanu ladowania; w stanie wyniku
        // zjadaly 160 px pustki i na telefonie spychaly wynik pod krawedz ekranu.
        preloader.classList.remove('active');
        preloaderData.textContent = '0%';
        resultsContainer.querySelectorAll('.result-message').forEach(el => el.remove());
        setMiniStep(null);

        pushDL('audit_completed', {
            audit_score: typeof data.score === 'number' ? data.score : null,
            audit_query: document.getElementById('siteUrl2').textContent
        });

        const results = document.createElement('div');
        results.classList.add('app__finally');
        results.innerHTML = typeof data.data === 'string' ? data.data : (data.data && data.data.html ? data.data.html : '');

        let wrapper = results.querySelector('.finally-results');
        if (!wrapper) {
            // Silnik oddal same karty: opakowujemy je, zeby bramka miala gdzie wejsc.
            wrapper = document.createElement('div');
            wrapper.className = 'finally-results';
            while (results.firstChild) wrapper.appendChild(results.firstChild);
            results.appendChild(wrapper);
        }
        // Kolejnosc (pinezki 22 i 25, 20.09): najpierw pasek z adresem badanej strony
        // i wezwaniami, pod nim panel wyniku. Panel przychodzacy z silnika kasujemy,
        // bo ten sam komplet liczb stal wczesniej na ekranie dwa razy.
        const appContent = document.querySelector('.app__content');
        const naglowek = zbudujNaglowekWyniku(data) || wrapper.querySelector('.wcag-wynik');
        if (naglowek) {
            const zSilnika = wrapper.querySelector('.wcag-wynik');
            if (zSilnika && zSilnika !== naglowek) zSilnika.remove();
            // Ta sama lista trzech problemow przychodzi z silnika i stoi juz w panelu
            // wyniku (pinezka 26, 20.09). Zostawiamy jedna, ta na gorze.
            wrapper.querySelectorAll('.wcag-top3').forEach((lista) => {
                const tytul = lista.previousElementSibling;
                if (tytul && /najwa/i.test(tytul.textContent || '')) tytul.remove();
                lista.remove();
            });
            naglowek.classList.add('wcag-wynik--gora');
            appContent.insertBefore(naglowek, appHeader.nextSibling);
        }
        const pasek = document.querySelector('.app__bar');
        const gotoLink = pasek ? pasek.querySelector('.app__gotoform') : null;
        if (gotoLink) {
            gotoLink.classList.add('mainBtn', 'app__gotoform--naglowek');
            appHeader.appendChild(gotoLink);
        }
        if (pasek) pasek.hidden = true;

        appContent.appendChild(results);
        // Ekran wyniku wchodzi w normalny przeplyw strony (pinezka 32): nakladka fixed
        // przykrywala stopke i uchwyt zgody, ktore na tym ekranie maja byc widoczne.
        document.querySelector('.app').classList.add('app--wynik');
        dodajOdnosnikDoWyniku();
        addEmailForm();
        policzWynik(appContent);   // liczba stoi juz nad naglowkiem, nie w results

        const scroll_to_form = document.querySelector('.app__gotoform');
        if (scroll_to_form) {
            scroll_to_form.addEventListener('click', (e) => {
                e.preventDefault();
                przewinDoBramki();
            });
        }
        if (appHeading) window.setTimeout(() => appHeading.focus(), 50);
    }

    /** Duza liczba wyniku: odliczanie od zera (jedna klatka przy reduced-motion). */
    function policzWynik(zakres) {
        const b = zakres.querySelector('.wcag-wynik__liczba b[data-to]');
        if (!b) return;
        const to = parseInt(b.dataset.to, 10) || 0;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            b.textContent = String(to);
            return;
        }
        let t0 = null;
        const dur = 1400;
        function step(t) {
            if (!t0) t0 = t;
            let p = Math.min(1, (t - t0) / dur);
            p = 1 - Math.pow(1 - p, 3);
            b.textContent = String(Math.round(to * p));
            if (p < 1) window.requestAnimationFrame(step);
        }
        window.requestAnimationFrame(step);
    }

    function przewinDoBramki() {
        const pole = document.querySelector('#emailForm input[type="email"]');
        const formularz = document.getElementById('emailForm');
        if (!formularz) return;
        formularz.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (!pole) return;
        // Podswietlenie zamiast focus: na telefonie focus podnosi klawiature
        // i zaslania to, po co odbiorca tu przyszedl.
        pole.classList.add('pole--wskazane');
        window.setTimeout(() => pole.classList.remove('pole--wskazane'), 2200);
    }

    // --- bramka e-mail ------------------------------------------------------

    function addEmailForm() {
        const emailForm = document.createElement('div');
        emailForm.classList.add('finally-results__form');

        // TRESC_ZGODY: identyczna z api.php silnika. Zmiana = w obu miejscach naraz
        // + podbicie WERSJA_ZGODY w api.php.
        emailForm.innerHTML = `
            <h3 class="finally-results__intro">Odbierz pełny raport PDF</h3>
            <p class="finally-results__intro">Wszystkie bariery z fragmentami kodu i wskazówką naprawy oraz plan prac, który przekażesz wykonawcy strony.</p>
            <form id="emailForm" novalidate>
                <div class="emailForm__form">
                    <div class="form-group">
                        <label for="userEmail" class="emailForm__label">Adres e-mail, na który wyślemy raport</label>
                        <input type="email" id="userEmail" name="email" autocomplete="email" placeholder="np. biuro@twojafirma.pl" aria-describedby="userEmailError" >
                        <div class="error-msg" id="userEmailError" role="alert"></div>
                    </div>
                    <div class="form-group checkbox-group">
                        <input type="checkbox" id="rodoConsent" aria-describedby="rodoConsentError" >
                        <label for="rodoConsent"><span>

    Akceptuję <a href="https://wcag.avangardo.pl/regulamin-uslugi/" target="_blank" rel="noopener">Regulamin usługi wcag.avangardo.pl</a> i wyrażam zgodę na otrzymywanie od "Avangardo sp. z o.o." treści marketingowych i informacji handlowych, dotyczących produktów i usług "Avangardo sp. z o.o." poprzez kontakt e-mailowy. Możesz nie podawać danych do kontaktu handlowego i marketingowego i uzyskać raport w kwocie 99 zł. W tym celu napisz do nas na marcin@avangardo.pl.

    <span class="rodo-hidden-element" id="rodoWiecej">Administratorem Twoich danych osobowych jest „Avangardo spółka z o.o.", z siedzibą w Szczecinie 70-655, przy ul. Wendy 10 lok. 3C, wpisaną do Krajowego Rejestru Sądowego pod numerem KRS: 0000942472, REGON: 520784536, NIP: 8513268189. Możesz wycofać zgodę w dowolnym momencie, kierując informację na marcin@avangardo.pl. Wycofanie zgody nie wpływa na zgodność z prawem działań podjętych na podstawie zgody przed jej wycofaniem. Więcej w <a href="https://wcag.avangardo.pl/polityka-prywatnosci/" target="_blank" rel="noopener">polityce prywatności</a>.</span>
                        </span>
                        </label>
                        <button type="button" class="rodo-hidden-element-trigger" aria-expanded="false" aria-controls="rodoWiecej">Rozwiń więcej</button>
                        <div class="error-msg" id="rodoConsentError" role="alert"></div>
                    </div>
                <button type="submit" class="btn mainBtn">Odbieram raport PDF</button>
                </div>
                <div class="emailForm__generator" aria-live="polite"></div>
            </form>

        `;

        document.querySelector('.finally-results').appendChild(emailForm);

        const emailFormElement = document.getElementById('emailForm');
        const emailInput = document.getElementById('userEmail');
        const rodoConsent = document.getElementById('rodoConsent');
        const rodoHiddenElement = document.querySelector('.rodo-hidden-element');
        const rodoHiddenElementTrigger = document.querySelector('.rodo-hidden-element-trigger');
        rodoHiddenElementTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            rodoHiddenElement.classList.toggle('show');
            const otwarte = rodoHiddenElement.classList.contains('show');
            rodoHiddenElementTrigger.textContent = otwarte ? 'Zwiń' : 'Rozwiń więcej';
            rodoHiddenElementTrigger.setAttribute('aria-expanded', otwarte ? 'true' : 'false');
        });

        emailFormElement.addEventListener('submit', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (!validateEmail(emailInput.value)) {
                showInputError(emailInput, 'Wpisz poprawny adres e-mail, np. biuro@twojafirma.pl');
                emailInput.focus();
                return;
            } else {
                clearInputError(emailInput);
            }

            if (!rodoConsent.checked) {
                showInputError(rodoConsent, 'Zaznacz zgodę, żebyśmy mogli wysłać raport');
                rodoConsent.focus();
                return;
            } else {
                clearInputError(rodoConsent);
            }

            const email = emailInput.value.trim();
            const emailForm__generator = document.querySelector('.emailForm__generator');
            const emailForm__form = document.querySelector('.emailForm__form');
            emailForm__form.classList.add('hide');
            emailForm__generator.classList.add('show');

            const progressContainer = document.createElement('div');
            progressContainer.className = 'report-progress';

            const barContainer = document.createElement('div');
            barContainer.className = 'report-progress__bar-container';
            const bar = document.createElement('div');
            bar.className = 'report-progress__bar';
            bar.id = 'reportProgressBar';
            bar.style.width = '0%';
            barContainer.appendChild(bar);

            const stepText = document.createElement('p');
            stepText.className = 'report-progress__step';
            stepText.id = 'reportProgressStep';
            stepText.textContent = 'Przygotowuję raport';

            const infoText = document.createElement('p');
            infoText.className = 'report-progress__info';
            infoText.textContent = 'Nie zamykaj tej strony. Raport wyślemy na Twój adres e-mail.';

            progressContainer.appendChild(barContainer);
            progressContainer.appendChild(stepText);
            progressContainer.appendChild(infoText);

            emailForm__generator.textContent = '';
            emailForm__generator.appendChild(progressContainer);

            let progressInterval = null;
            const progressBar = document.getElementById('reportProgressBar');
            const progressStep = document.getElementById('reportProgressStep');

            let bladOdpytania = false;
            progressInterval = setInterval(async () => {
                try {
                    const prog = await apiCall({ action: 'check_progress' }, 10000);
                    if (prog && prog.step) {
                        const pct = prog.percent || Math.round((prog.step / prog.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressStep.textContent = 'Krok ' + prog.step + ' z ' + prog.total + ': ' + prog.message;
                    }
                } catch (e) {
                    // Bledy odpytywania nie przerywaja generowania; do dziennika tylko pierwszy.
                    if (!bladOdpytania) { bladOdpytania = true; zalogujBlad('check_progress', e, ''); }
                }
            }, 1500);

            try {
                // Analiza AI + PDF + mail potrafia trwac ponad minute
                const data = await apiCall({ action: 'save_email', email: email, consent: true }, 180000);

                clearInterval(progressInterval);

                if (data.status === 'ok') {
                    progressBar.style.width = '100%';

                    // Meta Pixel: TU jest prawdziwy lead - odbiorca zostawil adres.
                    if (typeof fbq === 'function') {
                        fbq('track', 'Lead', {
                            content_name: document.getElementById('siteUrl2').textContent,
                            content_category: 'wcag_audit',
                            status: true
                        });
                    }
                    pushDL('report_submitted', {
                        audit_query: document.getElementById('siteUrl2').textContent
                    });

                    // Jedno brzmienie komunikatu na wszystkich audytorach (23.08.2026).
                    const successMsg = document.createElement('p');
                    successMsg.className = 'success-message';
                    successMsg.textContent = 'Raport został wysłany na Twój adres e-mail.';

                    const nextStep = document.createElement('p');
                    nextStep.className = 'success-next';
                    nextStep.textContent = 'Sprawdź także folder SPAM';

                    emailForm__generator.textContent = '';
                    emailForm__generator.appendChild(successMsg);
                    emailForm__generator.appendChild(nextStep);
                    // Po wysylce zachete "Odbierz pelny raport PDF" chowamy, jak na Radarze i LocalOne.
                    document.querySelectorAll('.finally-results__intro').forEach((el) => el.remove());
                } else {
                    // Odmowe (status error) zapisal juz serwer; tu tylko komunikat dla klienta.
                    const odmowa = new Error(data.message || 'Nie udało się wysłać raportu. Spróbuj ponownie.');
                    odmowa.zSerwera = true;
                    throw odmowa;
                }
            } catch (error) {
                clearInterval(progressInterval);
                emailForm__form.classList.remove('hide');
                emailForm__generator.classList.remove('show');
                // Komunikaty z odczytajOdpowiedz mowia o "badaniu"; tu klient czeka na raport.
                const komunikat = error.name === 'AbortError' || error.typ === 'timeout'
                    ? 'Generowanie raportu trwa dłużej niż zwykle. Sprawdź skrzynkę za kilka minut, zanim spróbujesz ponownie.'
                    : (error.zSerwera || error.typ === 'siec' ? error.message
                        : (error.typ ? 'Nie udało się przygotować raportu. Spróbuj ponownie za kilka minut.' : 'Nie udało się wysłać raportu. Spróbuj ponownie za chwilę.'));
                if (!error.zSerwera) zalogujBlad('save_email', error, komunikat);
                showInputError(emailInput, komunikat);
            }
        });

        function validateEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(String(email).trim().toLowerCase());
        }

        function showInputError(inputElement, message) {
            const formGroup = inputElement.closest('.form-group');
            const errorElement = formGroup.querySelector('.error-msg');
            errorElement.textContent = message;
            formGroup.classList.add('has-error');
            inputElement.setAttribute('aria-invalid', 'true');
        }

        function clearInputError(inputElement) {
            const formGroup = inputElement.closest('.form-group');
            const errorElement = formGroup.querySelector('.error-msg');
            errorElement.textContent = '';
            formGroup.classList.remove('has-error');
            inputElement.removeAttribute('aria-invalid');
        }
    }

    // --- licznik procentow ----------------------------------------------------

    let preloaderTimer = null;
    function ustawProcent(minimum) {
        const teraz = parseInt(preloaderData.textContent, 10) || 0;
        if (teraz < minimum) preloaderData.textContent = minimum + '%';
    }
    function updatePreloaderData(newValue, duration = 500) {
        if (preloaderTimer) clearInterval(preloaderTimer);
        const currentValue = parseInt(preloaderData.textContent, 10) || 0;
        if (newValue <= currentValue) return;
        const steps = Math.max(1, Math.round(duration / 40));
        const increment = (newValue - currentValue) / steps;

        let currentStep = 0;
        preloaderTimer = setInterval(() => {
            currentStep++;
            const value = Math.round(currentValue + increment * currentStep);
            preloaderData.textContent = `${value}%`;

            if (currentStep >= steps) {
                clearInterval(preloaderTimer);
                preloaderTimer = null;
            }
        }, 40);
    }
}
